import {
  Box,
  FormControl,
  FormHelperText,
  OutlinedInput,
  InputLabel,
  Typography,
  Grid,
  Stack,
} from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import validateInputStringCoordinates from '../../services/geoLocations/validateInputStringCoordinates'

const CoordinatesInput = ({ readOnly, selectedConfirmationSite }) => {
  const { control, setValue, getValues } = useFormContext()

  const validateLatitude = lat => {
    let isValid = true
    if (Number.isNaN(lat)) {
      isValid = false
    }
    if (lat < -90 || lat > 90) {
      isValid = false
    }
    return isValid || 'Latitud inválida'
  }

  const validateLongitude = lng => {
    let isValid = true
    if (!lng) {
      isValid = false
    }
    if (Number.isNaN(lng)) {
      isValid = false
    }
    if (lng < -180 || lng > 180) {
      isValid = false
    }
    return isValid || 'Longitud inválida'
  }

  const validateDecimals = value => {
    if (value === null || value === undefined || value === '') {
      return true // La validación required ya maneja esto
    }

    const valueStr = value.toString()
    const parts = valueStr.split('.')

    // Si no hay parte decimal, es inválido
    if (parts.length < 2) {
      return 'Al menos 5 decimales requeridos'
    }

    const decimalPart = parts[1]
    // Validar que tenga al menos 5 decimales (5 o más)
    if (decimalPart.length < 5) {
      return 'Al menos 5 decimales requeridos'
    }

    return true
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Grid container spacing={1}>
        <Grid item sm={6} xs={12}>
          <Controller
            valueAsNumber
            control={control}
            defaultValue={
              selectedConfirmationSite?.id === 'manual' ? '' : selectedConfirmationSite?.lat || ''
            }
            name="lat"
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                color={fieldState.error ? 'error' : 'primary'}
                error={!!fieldState.error}
                size="small"
                variant="outlined"
              >
                <InputLabel htmlFor="outlined-coords">Latitud</InputLabel>
                <OutlinedInput
                  {...field}
                  error={!!fieldState.error}
                  inputProps={{
                    min: -90,
                    max: 90,
                    step: 'any',
                  }}
                  label="Latitud"
                  placeholder="Latitud"
                  readOnly={readOnly}
                  type="number"
                  onBlur={event => {
                    // Llamar primero a field.onBlur() para que react-hook-form registre el campo y ejecute validaciones
                    field.onBlur()

                    if (event.target.value) {
                      const latNum = event.target.valueAsNumber
                      field.onChange(latNum)
                      const currentLng = getValues('lng')
                      // Solo setear actualLocation si ambos valores son números válidos
                      if (
                        currentLng !== null &&
                        currentLng !== undefined &&
                        !Number.isNaN(currentLng) &&
                        typeof currentLng === 'number'
                      ) {
                        setValue('actualLocation', [latNum, currentLng], { shouldValidate: true })
                      } else {
                        // Si lng no está disponible, setear actualLocation como null
                        setValue('actualLocation', null, { shouldValidate: true })
                      }
                    } else {
                      field.onChange(null)
                      // Si lat está vacío, actualLocation debe ser null
                      setValue('actualLocation', null, { shouldValidate: true })
                    }
                  }}
                />
                {fieldState.error && (
                  <FormHelperText color={fieldState.error ? 'error' : 'inherit'}>
                    {fieldState.error?.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
            rules={{
              required: 'Campo requerido',
              valueAsNumber: true,
              validate: {
                latitude: validateLatitude,
                decimals: validateDecimals,
              },
            }}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <Controller
            valueAsNumber
            control={control}
            defaultValue={
              selectedConfirmationSite?.id === 'manual' ? '' : selectedConfirmationSite?.lng || ''
            }
            name="lng"
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={!!fieldState.error} size="small" variant="outlined">
                <InputLabel htmlFor="outlined-coords">Longitud</InputLabel>
                <OutlinedInput
                  {...field}
                  error={!!fieldState.error}
                  inputProps={{
                    min: -180,
                    max: 180,
                    step: 'any',
                  }}
                  label="Longitud"
                  placeholder="Longitud"
                  readOnly={readOnly}
                  type="number"
                  onBlur={event => {
                    // Llamar primero a field.onBlur() para que react-hook-form registre el campo y ejecute validaciones
                    field.onBlur()

                    if (event.target.value) {
                      const lngNum = event.target.valueAsNumber
                      field.onChange(lngNum)
                      const currentLat = getValues('lat')
                      // Solo setear actualLocation si ambos valores son números válidos
                      if (
                        currentLat !== null &&
                        currentLat !== undefined &&
                        !Number.isNaN(currentLat) &&
                        typeof currentLat === 'number'
                      ) {
                        setValue('actualLocation', [currentLat, lngNum], { shouldValidate: true })
                      } else {
                        // Si lat no está disponible, setear actualLocation como null
                        setValue('actualLocation', null, { shouldValidate: true })
                      }
                    } else {
                      field.onChange(null)
                      // Si lng está vacío, actualLocation debe ser null
                      setValue('actualLocation', null, { shouldValidate: true })
                    }
                  }}
                />
                {fieldState.error && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
              </FormControl>
            )}
            rules={{
              required: 'Campo requerido',
              valueAsNumber: true,
              validate: {
                longitude: validateLongitude,
                decimals: validateDecimals,
              },
            }}
          />
        </Grid>
      </Grid>
      <Controller
        control={control}
        name="actualLocation"
        render={({ field, fieldState }) => (
          <Stack direction="row" display="none" gap={0.75} justifyContent="center">
            <Typography color={fieldState.error ? 'error' : 'inherit'} variant="bodySmall">
              {Array.isArray(field.value) && field.value.length > 0 ? field.value.join(', ') : ''}
            </Typography>
          </Stack>
        )}
        rules={{
          required: 'Requerido',
          validate: value => {
            if (!value || !Array.isArray(value) || value.length !== 2) {
              return 'Coordenadas inválidas'
            }
            const [lat, lng] = value
            if (typeof lat !== 'number' || typeof lng !== 'number') {
              return 'Coordenadas deben ser números'
            }
            if (Number.isNaN(lat) || Number.isNaN(lng)) {
              return 'Coordenadas inválidas'
            }
            // Convertir a string para la validación existente
            const coordString = `${lat}, ${lng}`
            return validateInputStringCoordinates(coordString) || 'Coordenadas inválidas'
          },
        }}
      />
    </Box>
  )
}

export default CoordinatesInput
